import { useState } from 'react'
import { useSendRequests } from '../hooks/useARE'
import { Mail, MailOpen, MousePointer, Star, AlertCircle, Clock } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

const statusCfg: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  pending:  { label: 'En attente', icon: Clock,        color: 'text-ink-faint bg-paper-warm' },
  sent:     { label: 'Envoyé',     icon: Mail,         color: 'text-blue-600 bg-blue-50' },
  opened:   { label: 'Ouvert',     icon: MailOpen,     color: 'text-amber-600 bg-amber-50' },
  clicked:  { label: 'Cliqué',     icon: MousePointer, color: 'text-orange-600 bg-orange-50' },
  reviewed: { label: 'Avis publié',icon: Star,         color: 'text-emerald-600 bg-emerald-50' },
  bounced:  { label: 'Rebond',     icon: AlertCircle,  color: 'text-red-600 bg-red-50' },
}

export function ActivityFeedPage() {
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [page, setPage] = useState(1)
  const { data, isLoading } = useSendRequests({ status: statusFilter, page })
  const items = data?.data ?? []
  const total = data?.meta?.total ?? 0

  const filters = [
    { value: undefined, label: 'Tous' },
    { value: 'sent', label: 'Envoyés' },
    { value: 'opened', label: 'Ouverts' },
    { value: 'clicked', label: 'Cliqués' },
    { value: 'reviewed', label: 'Avis publiés' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-bold text-ink tracking-tight">Activité</h1>
        <p className="text-ink-muted mt-1">Historique des demandes d'avis envoyées</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {filters.map((f) => (
          <button key={f.label} onClick={() => { setStatusFilter(f.value); setPage(1) }}
            className={cn('px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              statusFilter === f.value ? 'bg-brick text-white' : 'bg-paper-warm text-ink hover:bg-paper-warm')}>
            {f.label}
          </button>
        ))}
        <span className="text-sm text-ink-faint ml-2">{total} envoi(s)</span>
      </div>

      <div className="space-y-2">
        {isLoading ? (
          <div className="text-center py-12 text-ink-faint">Chargement…</div>
        ) : !items.length ? (
          <div className="text-center py-12 bg-white rounded-card border border-ink-border">
            <Mail size={40} className="mx-auto text-ink-faint mb-3" />
            <p className="text-ink-muted">Aucun envoi pour le moment</p>
          </div>
        ) : (
          items.map((sr: any) => {
            const cfg = statusCfg[sr.status] ?? statusCfg.pending
            return (
              <div key={sr.id} className="flex items-center gap-4 bg-white rounded-card border border-ink-border px-5 py-3.5">
                <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', cfg.color)}>
                  <cfg.icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink truncate">
                    {sr.contact_name || sr.contact_email || '—'}
                  </p>
                  <p className="text-xs text-ink-faint">
                    {sr.segment === 'promoter' ? '⭐ Promoteur' : '📋 Détracteur'}
                    {sr.sent_at && ` · Envoyé ${new Date(sr.sent_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
                  </p>
                </div>
                <span className={cn('text-xs font-medium px-2.5 py-1 rounded-full', cfg.color)}>
                  {cfg.label}
                </span>
                <div className="flex gap-3 text-[11px] text-ink-faint flex-shrink-0">
                  {sr.opened_at && <span>👁 {new Date(sr.opened_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>}
                  {sr.clicked_at && <span>🖱 {new Date(sr.clicked_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>}
                  {sr.reviewed_at && <span>⭐ {new Date(sr.reviewed_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>}
                </div>
              </div>
            )
          })
        )}
      </div>

      {total > 20 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
            className="px-3 py-1.5 rounded-lg bg-paper-warm text-sm disabled:opacity-40">← Précédent</button>
          <span className="text-sm text-ink-muted">Page {page}</span>
          <button onClick={() => setPage(page + 1)} disabled={items.length < 20}
            className="px-3 py-1.5 rounded-lg bg-paper-warm text-sm disabled:opacity-40">Suivant →</button>
        </div>
      )}
    </div>
  )
}
